/**
 * Briefing pós-atendimento pra secretária.
 *
 * Quando um atendimento vai pra CONCLUIDA, calculamos o que foi
 * solicitado durante a consulta (exames, cirurgias, retorno) lendo as
 * prescriptions do paciente criadas dentro da janela do atendimento
 * (`attendanceStartedAt` → `attendanceEndedAt + tolerância`).
 *
 * Quando há alguma coisa a orientar, gravamos `secretaryBriefing` no
 * próprio appointment com `briefed=false`. A Caixa de Pendências escuta
 * em tempo real os appointments com briefing não-orientado e mostra
 * uma seção "Atendimentos finalizados — orientar paciente". Quando a
 * secretária clica em "Paciente orientado", marcamos `briefed=true` e
 * o item sai do painel.
 *
 * Sem `appointmentId` nas prescriptions, usamos a janela temporal +
 * patientId como chave aproximada. Sobreposições só acontecem se o
 * mesmo paciente tem duas consultas simultâneas (caso improvável). A
 * tolerância de 5min no fim cobre o "salvei a prescrição um instante
 * depois de marcar Concluir".
 */
import { db } from './config';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  updateDoc,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import type { Appointment, Prescription } from '@/types/models';

/** Margem após `attendanceEndedAt` pra capturar prescrições salvas
 *  imediatamente após "Concluir atendimento". */
const POST_END_TOLERANCE_MS = 5 * 60 * 1000; // 5 minutos

/** Fallback quando não há `attendanceStartedAt` (ex.: status mudou direto
 *  pra CONCLUIDA pelo modal de detalhes sem passar pelo cronômetro). */
const NO_TIMER_LOOKBACK_MS = 4 * 60 * 60 * 1000; // 4 horas

/** Janela de visibilidade da pendência (briefings mais antigos que isso
 *  somem do painel pra evitar acumular ruído mesmo sem secretária marcar). */
const PENDING_VISIBILITY_MS = 7 * 24 * 60 * 60 * 1000; // 7 dias

export interface BriefingSnapshot {
  hasExams: boolean;
  hasProcedures: boolean;
  hasReturn: boolean;
  prescriptionIds: string[];
  createdAt: number;
  briefed: boolean;
}

/**
 * Conta um EXAM_REQUEST como "retorno puro" (sem exames) quando o
 * array de exams está vazio ou ausente. Esse é o caso da "Solicitação
 * de Retorno" criada pelo médico em `prescriptions.ts:142-147` quando
 * preenche orientações sem listar exames.
 */
function isReturnOnlyExamRequest(p: Prescription): boolean {
  if (p.type !== 'EXAM_REQUEST') return false;
  const exams = (p.content as { exams?: unknown[] } | undefined)?.exams;
  return !Array.isArray(exams) || exams.length === 0;
}

/**
 * Calcula a janela temporal do atendimento. Quando o cronômetro foi
 * usado, fica entre `attendanceStartedAt` e `attendanceEndedAt`. Caso
 * contrário, usa as últimas 4h até agora — captura o caso de
 * finalização direta pelo modal sem o cronômetro do AttendanceTimerCard.
 */
function resolveWindow(appointment: Appointment): { start: number; end: number } {
  const end = (appointment.attendanceEndedAt ?? Date.now()) + POST_END_TOLERANCE_MS;
  const start = appointment.attendanceStartedAt ?? (end - NO_TIMER_LOOKBACK_MS);
  return { start, end };
}

export const SecretaryBriefingService = {
  /**
   * Lê as prescriptions do paciente e classifica o que foi pedido na
   * janela do atendimento. Retorna `null` quando nada foi solicitado —
   * caller decide não persistir nesse caso (ver `finalizeBriefing`).
   *
   * Filtra por `professionalId` em memória pra evitar índice composto
   * extra; o volume típico de prescriptions por paciente é baixo.
   */
  async buildBriefing(
    clinicId: string,
    appointment: Appointment,
  ): Promise<BriefingSnapshot | null> {
    const { start, end } = resolveWindow(appointment);

    const colRef = collection(db, 'clinics', clinicId, 'prescriptions');
    const q = query(
      colRef,
      where('patientId', '==', appointment.patientId),
      where('type', 'in', ['EXAM_REQUEST', 'SURGERY']),
    );
    const snap = await getDocs(q);

    const inWindow = snap.docs
      .map((d) => ({ id: d.id, ...d.data() } as Prescription))
      .filter((p) => {
        if (typeof p.createdAt !== 'number') return false;
        if (p.createdAt < start || p.createdAt > end) return false;
        // Quando o appointment tem profissional, exige bater. Sem
        // profissional, aceita qualquer prescription do paciente na
        // janela (ainda restrito pelo intervalo, então é seguro).
        if (appointment.professionalId && p.professionalId && p.professionalId !== appointment.professionalId) {
          return false;
        }
        return true;
      });

    if (inWindow.length === 0) return null;

    const hasProcedures = inWindow.some((p) => p.type === 'SURGERY');
    const hasExams = inWindow.some(
      (p) => p.type === 'EXAM_REQUEST' && !isReturnOnlyExamRequest(p),
    );
    const hasReturn = inWindow.some(isReturnOnlyExamRequest);

    if (!hasExams && !hasProcedures && !hasReturn) return null;

    return {
      hasExams,
      hasProcedures,
      hasReturn,
      prescriptionIds: inWindow.map((p) => p.id),
      createdAt: Date.now(),
      briefed: false,
    };
  },

  /**
   * Persiste o briefing no appointment. Não-op quando snapshot é null
   * (consulta sem pedidos) pra não poluir a caixa de pendências.
   */
  async persistBriefing(
    clinicId: string,
    appointmentId: string,
    snapshot: BriefingSnapshot | null,
  ): Promise<void> {
    if (!snapshot) return;
    const ref = doc(db, 'clinics', clinicId, 'appointments', appointmentId);
    await updateDoc(ref, { secretaryBriefing: snapshot });
  },

  /**
   * Conveniência: lê o appointment atual (pra ter `attendanceEndedAt`
   * já gravado), constrói o briefing e persiste. Chamado pelos pontos
   * de finalização (endAttendance + AppointmentDetailModal).
   *
   * Falhas no briefing **não** propagam — finalizar atendimento é a
   * ação principal e não deve ser bloqueada por erro de leitura de
   * prescription. Loga e segue.
   *
   * Lida com retomada de atendimento: se o médico reabriu a consulta
   * (via "Retomar" no AttendanceTimerCard) e refinalizou, comparamos
   * o conjunto de prescription IDs antigo com o novo cálculo da janela:
   *   - Sem nada novo: preserva o briefing antigo intacto (não
   *     reabre alerta nem apaga "Paciente orientado" da secretária).
   *   - Com prescription nova: sobrescreve o briefing (briefed
   *     volta a false, secretária re-orienta com a lista atualizada).
   */
  async finalizeBriefing(clinicId: string, appointmentId: string): Promise<void> {
    try {
      const ref = doc(db, 'clinics', clinicId, 'appointments', appointmentId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return;
      const appointment = { id: appointmentId, ...snap.data() } as Appointment;
      const newBriefing = await this.buildBriefing(clinicId, appointment);
      const oldBriefing = appointment.secretaryBriefing;

      // Sem briefing antigo: persiste o novo (no-op se newBriefing=null).
      // Caminho da primeira finalização normal.
      if (!oldBriefing) {
        await this.persistBriefing(clinicId, appointmentId, newBriefing);
        return;
      }

      // Tinha briefing, mas a janela atual não encontrou nada — improvável
      // (prescriptions não somem por conta própria). Por segurança,
      // preserva o briefing antigo em vez de apagar.
      if (!newBriefing) return;

      // Tinha briefing E tem novo: decide pelo delta de prescription IDs.
      const oldIds = new Set(oldBriefing.prescriptionIds ?? []);
      const hasNewPrescription = newBriefing.prescriptionIds.some(
        (id) => !oldIds.has(id),
      );

      if (!hasNewPrescription) {
        // Nada novo solicitado — preserva briefing antigo intacto, incluindo
        // a confirmação "Paciente orientado" da secretária. Sem isso, retomar
        // + reconcluir sem mudar nada faria a pendência reaparecer
        // injustamente.
        return;
      }

      // Médico solicitou algo novo nessa rodada (retomada + nova prescription).
      // Sobrescreve o briefing — `briefed` volta a false, secretária re-orienta
      // já com os novos pedidos contemplados.
      await this.persistBriefing(clinicId, appointmentId, newBriefing);
    } catch (err) {
      console.error('[secretary-briefing] finalize failed:', err);
    }
  },

  /**
   * Marca o briefing como orientado. Usa dotted-path no updateDoc pra
   * mexer só nos sub-campos relevantes — preserva `hasExams` etc.
   */
  async markBriefed(
    clinicId: string,
    appointmentId: string,
    actor: { uid: string; name?: string },
  ): Promise<void> {
    const ref = doc(db, 'clinics', clinicId, 'appointments', appointmentId);
    const payload: Record<string, unknown> = {
      'secretaryBriefing.briefed': true,
      'secretaryBriefing.briefedAt': Date.now(),
      'secretaryBriefing.briefedBy': actor.uid,
    };
    if (actor.name) {
      payload['secretaryBriefing.briefedByName'] = actor.name;
    }
    await updateDoc(ref, payload);
  },

  /**
   * Listener em tempo real dos briefings pendentes (status=CONCLUIDA,
   * briefed=false). Filtra por idade em memória — appointments mais
   * antigos que 7 dias somem do painel pra não acumular ruído quando
   * a secretária esquece de marcar.
   *
   * Retorna o `Unsubscribe` do onSnapshot pro caller limpar no unmount.
   */
  subscribePendingBriefings(
    clinicId: string,
    callback: (items: Appointment[]) => void,
    options?: { visibilityMs?: number },
  ): Unsubscribe {
    const visibilityMs = options?.visibilityMs ?? PENDING_VISIBILITY_MS;
    const q = query(
      collection(db, 'clinics', clinicId, 'appointments'),
      where('status', '==', 'CONCLUIDA'),
      where('secretaryBriefing.briefed', '==', false),
    );
    return onSnapshot(
      q,
      (snap) => {
        const cutoff = Date.now() - visibilityMs;
        const items = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as Omit<Appointment, 'id'>) }))
          .filter((a) => {
            const created = a.secretaryBriefing?.createdAt ?? a.attendanceEndedAt ?? 0;
            return created >= cutoff && !a.isDeleted;
          })
          .sort((a, b) => {
            const at = a.secretaryBriefing?.createdAt ?? 0;
            const bt = b.secretaryBriefing?.createdAt ?? 0;
            return bt - at; // mais recente primeiro
          });
        callback(items);
      },
      (err) => {
        console.error('[secretary-briefing] subscribe error:', err);
      },
    );
  },
};
